import dayjs from "global/dayjs";
import { ChatMessage } from "app/modules/aiAssistant/AIAssistantSlice";
import useFollowLatestMessage from "app/modules/aiAssistant/conversation/messages/hooks/useFollowLatestMessage";
import MessageBody from "app/modules/aiAssistant/conversation/messages/components/MessageBody";
import SuggestedPrompts from "app/modules/aiAssistant/conversation/messages/components/SuggestedPrompts";
import RunSteps from "app/modules/aiAssistant/conversation/messages/components/RunSteps";
import { ASSISTIMO_ICON_SRC } from "app/modules/aiAssistant/shared/constants";
import DocumentChips from "app/modules/aiAssistant/conversation/components/DocumentChips";
import CopySupportDetailsButton from "app/modules/aiAssistant/shared/components/CopySupportDetailsButton";
import MessageFeedback, {
  FeedbackControl,
} from "app/modules/aiAssistant/conversation/messages/components/feedback/MessageFeedback";
import "app/modules/aiAssistant/styles/shared.scss";
import "./MessageList.scss";

type Props = {
  messages: ChatMessage[];
  isAwaitingReply: boolean;
  runSteps: string[];
  getCopyButtonTestId: (messageID: number) => string;
  getFeedbackTestId: (control: FeedbackControl, messageID: number) => string;
  onSuggestedPromptSelect: (promptText: string) => void;
  getSuggestedPromptTestId: (promptID: string) => string;
  thinkingToggleTestId: string;
};

const MessageList = ({
  messages,
  isAwaitingReply,
  runSteps,
  getCopyButtonTestId,
  getFeedbackTestId,
  onSuggestedPromptSelect,
  getSuggestedPromptTestId,
  thinkingToggleTestId,
}: Props) => {
  const { messageListRef, handleScroll } = useFollowLatestMessage(messages, isAwaitingReply, runSteps);
  const hasUserMessage = messages.some((m) => m.isUserAuthor);

  return (
    <div ref={messageListRef} onScroll={handleScroll} className="assistimo-messages">
      <div className="assistimo-readable-column">
        {messages.map((m, index) => {
          const isSavedReply = !m.isUserAuthor && m.messageID !== undefined;

          return (
            <div
              // Messages not saved yet have no ID
              key={m.messageID ?? `unsaved-${index}`}
              className={`assistimo-message ${m.isUserAuthor ? "assistimo-message--user" : ""} ${
                hasQueryResults(m) ? "assistimo-message--with-results" : ""
              }`}
            >
              {!m.isUserAuthor && <AssistimoAvatar />}
              <div className="assistimo-message__body">
                {m.documents && (
                  <div className="assistimo-message__documents">
                    <DocumentChips documents={m.documents} />
                  </div>
                )}
                <div className="assistimo-message__bubble">
                  <MessageBody content={m.content} queryResults={m.queryResults} />
                </div>
                <div className={`assistimo-message__meta ${m.feedback ? "assistimo-message__meta--rated" : ""}`}>
                  <div className="assistimo-message__time">{m.timestamp ? dayjs(m.timestamp).format("HH:mm") : ""}</div>
                  {isSavedReply && (
                    <CopySupportDetailsButton
                      messageID={m.messageID}
                      messageTimestamp={m.timestamp}
                      data-test-id={getCopyButtonTestId(m.messageID!)}
                    />
                  )}
                  {isSavedReply && (
                    <MessageFeedback
                      messageID={m.messageID!}
                      feedback={m.feedback}
                      getTestId={(control) => getFeedbackTestId(control, m.messageID!)}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!hasUserMessage && !isAwaitingReply && (
          <SuggestedPrompts onSelect={onSuggestedPromptSelect} getTestId={getSuggestedPromptTestId} />
        )}
        {isAwaitingReply && (
          <div className="assistimo-message">
            <AssistimoAvatar />
            <div className="assistimo-message__bubble" aria-label="Assistimo is replying">
              <RunSteps steps={runSteps} data-test-id={thinkingToggleTestId} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AssistimoAvatar = () => <img src={ASSISTIMO_ICON_SRC} className="assistimo-message__avatar" alt="Assistimo" />;

const hasQueryResults = (message: ChatMessage) => Object.keys(message.queryResults ?? {}).length > 0;

export default MessageList;
